import { DateTime } from "luxon";
const UNAVAILABLE_TEXT = "Unavailable";

/**
 * @description Helper function that will capitalize the first letter of a string
 * @param str string to capitalize
 * @returns string - the string with the first letter capitalized
 */
export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};


export type BooleanMutation<T> = {
  [key in keyof T]: boolean;
}



/**
 * @description Helper function that will compare two objects and return a map of the differences as boolean values
 * @param objectOne - the first object to compare
 * @param objectTwo - the second object to compare
 * @returns BooleanMutation<Type> - a map of the differences between the two objects
 */
export const objectDiff = <Type>(objectOne: Type, objectTwo: Type): BooleanMutation<Type> => {
  const diffMap: BooleanMutation<Type> = {} as BooleanMutation<Type>;

  for (const key in objectOne) {
    diffMap[key] = objectOne[key] !== objectTwo[key];
  }

  return diffMap;
};

/**
 * @description Helper function that will check if any of the values in a BooleanMutation object are true
 * @param obj - the object to check for differences
 * @param options - an optional object that can include an array of keys to exclude from the check
 * @returns boolean - true if any of the values in the object are true
 */
export const hasDiff = <Type>(obj: BooleanMutation<Type> | null, options?: { exclude: string[] }): boolean => {
  if (!obj) return false;

  const diffValues = Object.values(obj);
  const exclude = options?.exclude ?? [];

  return diffValues.some((value, index) => value && !exclude.includes(Object.keys(obj)[index]));
};

/**
 * @description Helper function that will return the first two initials of a string
 * @param name A string to get the initials from
 * @returns string - the initials of the string provided
 */
export const getInitials = (name: string): string => {
  if (!name) return "N/A";
  return name.replace(/[^A-Za-z0-9À-ÿ ]/g, '')
    .replace(/ +/ig, ' ')
    .split(/ /)
    .reduce((acc, item) => acc + item[0], '')
    .concat(name.substring(1))
    .substring(0, 2)
    .toUpperCase();
};


/**
 * @description Helper function that will compare two arrays of strings and return a boolean value if they are the same
 * @param arr1  the first array to compare
 * @param arr2  the second array to compare
 * @returns  boolean - true if the arrays are the same
 */
export const compareStringArrays = (arr1: string[], arr2: string[]): boolean => {
  if (arr1.length !== arr2.length) return false;

  return arr1.every((item) => arr2.includes(item));
};


/**
 * @description Helper function that will return a formatted date string
 * @param date - the date string to format
 * @param format - the format to use when formatting the date following the Luxon DateTime format pattern
 * @param unavailableText - the text to return if the date is unavailable
 * @param prefix - the text to add to the beginning of the formatted date
 * @param postfix - the text to add to the end of the formatted date
 * @returns string - the formatted date string
 */
export const formatDate = ({
  date,
  format,
  unavailableText = UNAVAILABLE_TEXT,
  prefix = "",
  postfix = "",
}: {
  date: string;
  format: string;
  unavailableText?: string;
  prefix?: string;
  postfix?: string;
}): string => {
  if (!date) return unavailableText;
  return (
    prefix + DateTime.fromMillis(Date.parse(date)).toFormat(format) + postfix
  );
};

/**
 * @description Helper function that will return a formatted date string from a number of milliseconds since epoch
 * @param millis - the number of milliseconds since epoch
 * @returns string - the formatted date string in the format of "M D, Y, HH:MM:SS AM/PM"
 * @example formatMillisDate(1712985600000) // "Apr 8, 2025, 10:15 AM"
 */
export const formatMillisDate = (millis: number) => {
  return DateTime.fromMillis(millis).toFormat("ff");
};


/**
 * @description Helper function that will return a query string
 * @param key - the key to get the value for
 * @returns - the value of the key in the query string
 */
export const getQueryString = (key: string) =>
  new URLSearchParams(window.location.search).get(key);
